import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import { Enemy } from '../entities/Enemy';
import { EliteEnemy } from '../entities/EliteEnemy';
import { Gate } from '../entities/Gate';
import { WeaponBox } from '../entities/WeaponBox';
import {
  WeaponType,
  WEAPON_SEQUENCE,
  getTierScaledBasicHp,
  getTierScaledEliteHp,
} from '../utils/constants';
import { getNextWeaponIndexForLevel } from './weaponBalance';
import {
  CompiledEnemyEvent,
  CompiledGateEvent,
  CompiledWeaponBoxEvent,
  GateOperation,
  GeneratedLevelConfig,
  createDeterministicLevelSeed,
  generateLevelConfig,
  getDeterministicRollForEvent,
} from '../levels/LevelGenerator';
import { getEnemyLane4X, getGateLaneX } from '../levels/LevelData';
import { FINALE_PRESENTATION } from '../levels/gameplayBalance';
import { buildNeutralDifficultyAdjustment } from './spawnDifficulty';
import {
  advanceProgressDistance,
  canResolveLevel,
  updateAndCompactEntities,
} from './spawn/entityLifecycle';

export interface SpawnSystemConfig {
  scene: Phaser.Scene;
  level: number;
  tier?: number;
  seed?: number;
  getScrollSpeed: () => number;
  getIsPaused: () => boolean;
  getCurrentArmySize?: () => number;
  trackWidth?: number;
  trackPadding?: number;
}

export class SpawnSystem {
  private readonly scene: Phaser.Scene;
  private level: number;
  private tier: number;
  private seed: number;

  private readonly getScrollSpeed: () => number;
  private readonly getIsPaused: () => boolean;
  private readonly getCurrentArmySize: () => number;

  private readonly trackWidth: number;
  private readonly trackPadding: number;

  private generatedLevel: GeneratedLevelConfig;
  private nextSegmentAdjustmentIndex: number = 0;

  private progressDistance: number = 0;
  private isLevelComplete: boolean = false;
  private finaleDefeated: boolean = false;

  private enemySpawnIndex: number = 0;
  private gateSpawnIndex: number = 0;
  private weaponBoxSpawnIndex: number = 0;

  private activeEnemies: Enemy[] = [];
  private activeGates: Gate[] = [];
  private activeWeaponBoxes: WeaponBox[] = [];

  private currentWeaponIndex: number = 0;
  private gateRows: Map<number, Gate[]> = new Map();

  private readonly events: Phaser.Events.EventEmitter;

  private enemyPool: Enemy[] = [];
  private static readonly POOL_SIZE = 60;

  private currentSegmentId: string = 'onboard';
  private lastArmyRatio: number = 1;
  private lastDdaDelta: number = 0;

  constructor(config: SpawnSystemConfig) {
    this.scene = config.scene;
    this.level = config.level;
    this.tier = config.tier ?? 1;
    this.seed = config.seed ?? createDeterministicLevelSeed(this.level, this.tier);
    this.getScrollSpeed = config.getScrollSpeed;
    this.getIsPaused = config.getIsPaused;
    this.getCurrentArmySize = config.getCurrentArmySize ?? (() => 1);
    this.trackWidth = config.trackWidth ?? GAME_WIDTH - 40;
    this.trackPadding = config.trackPadding ?? 20;

    this.events = new Phaser.Events.EventEmitter();
    this.generatedLevel = generateLevelConfig(this.level, this.tier, this.seed);

    this.initializePool();
  }

  private initializePool(): void {
    for (let i = 0; i < SpawnSystem.POOL_SIZE; i++) {
      const enemy = new Enemy(this.scene, { x: -100, y: -100, pooled: true });
      enemy.setActive(false);
      enemy.setVisible(false);
      this.enemyPool.push(enemy);
    }
  }

  update(delta: number): void {
    if (this.getIsPaused()) return;
    if (this.isLevelComplete) return;

    const scrollSpeed = this.getScrollSpeed();
    this.progressDistance = advanceProgressDistance(this.progressDistance, scrollSpeed, delta);

    this.applySegmentBoundaryAdjustments();
    this.checkEnemySpawns();
    this.checkGateSpawns();
    this.checkWeaponBoxSpawns();
    this.checkLevelComplete();

    this.updateActiveEnemies(delta);
    this.updateActiveGates(delta);
    this.updateActiveWeaponBoxes(delta);
  }

  private applySegmentBoundaryAdjustments(): void {
    const segments = this.generatedLevel.segments;
    while (this.nextSegmentAdjustmentIndex < segments.length) {
      const segment = segments[this.nextSegmentAdjustmentIndex];
      if (this.progressDistance < segment.startDistance) break;

      const currentArmy = this.getCurrentArmySize();
      const expectedArmy = Math.max(1, segment.expectedArmy);
      const armyRatio = currentArmy / expectedArmy;
      const adjustment = buildNeutralDifficultyAdjustment(segment.id, segment.index, armyRatio);
      this.currentSegmentId = segment.id;
      this.lastArmyRatio = adjustment.armyRatio;
      this.lastDdaDelta = adjustment.ddaDelta;
      this.nextSegmentAdjustmentIndex += 1;
    }
  }

  private checkEnemySpawns(): void {
    const events = this.generatedLevel.enemyEvents;
    const scrollSpeed = this.getScrollSpeed();

    while (this.enemySpawnIndex < events.length) {
      const event = events[this.enemySpawnIndex];
      if (this.progressDistance < this.getAdjustedTriggerDistance(event)) break;

      const enemy = this.spawnEnemyFromEvent(event, scrollSpeed);
      if (enemy) {
        this.activeEnemies.push(enemy);
        this.events.emit('enemySpawned', enemy, event.segmentIndex);
      }
      this.enemySpawnIndex += 1;
    }
  }

  private spawnEnemyFromEvent(event: CompiledEnemyEvent, scrollSpeed: number): Enemy | null {
    const x = getEnemyLane4X(event.lane, this.trackWidth, this.trackPadding);
    const y = event.isFinale ? FINALE_PRESENTATION.spawnY : -20;

    let enemy: Enemy;
    if (event.type === 'elite') {
      const baseHp = event.hp ?? Math.max(5, 4 + this.level);
      const scaledHp = event.isFinale
        ? baseHp
        : getTierScaledEliteHp(baseHp, this.tier);
      enemy = new EliteEnemy(this.scene, {
        x,
        y,
        hp: scaledHp,
        isFinale: event.isFinale,
        speed: event.isFinale ? FINALE_PRESENTATION.speedMultiplier : undefined,
      });
    } else {
      const scaledHp = getTierScaledBasicHp(this.tier);
      if (scaledHp > 1) {
        enemy = new Enemy(this.scene, { x, y, hp: scaledHp });
      } else {
        enemy = this.getFromPool(x, y);
      }
    }

    enemy.setScrollSpeed(scrollSpeed);
    enemy.once('died', (points: number) => {
      if (event.isFinale) {
        this.finaleDefeated = true;
        this.events.emit('finaleDefeated', enemy);
      }
      this.events.emit('enemyKilled', enemy, points);
    });

    if (!(enemy instanceof EliteEnemy) && this.tier === 1) {
      enemy.once('readyForPool', () => this.returnToPool(enemy));
    }

    return enemy;
  }

  private checkGateSpawns(): void {
    const events = this.generatedLevel.gateEvents;
    while (this.gateSpawnIndex < events.length) {
      const event = events[this.gateSpawnIndex];
      if (this.progressDistance < this.getAdjustedTriggerDistance(event)) break;
      this.spawnGateRow(event, this.gateSpawnIndex);
      this.gateSpawnIndex += 1;
    }
  }

  private spawnGateRow(event: CompiledGateEvent, rowIndex: number): void {
    const rowY = event.spawnY ?? -Gate.HEIGHT;
    const scrollSpeed = this.getScrollSpeed();
    const rowGates: Gate[] = [];

    for (let optionIndex = 0; optionIndex < event.options.length; optionIndex++) {
      const sourceOption = event.options[optionIndex];
      const x = getGateLaneX(sourceOption.lane, this.trackWidth, this.trackPadding);

      const gate = new Gate(this.scene, {
        x,
        y: rowY,
        operation: sourceOption.operation,
      });
      gate.setScrollSpeed(scrollSpeed);

      gate.on(
        'activated',
        (
          _multiplierLike: number,
          soldiersDelta: number,
          scoreFactor: number,
          gateOperation?: GateOperation
        ) => {
          const resolvedOperation = gateOperation ?? gate.getOperation();
          for (const otherGate of rowGates) {
            if (otherGate !== gate && otherGate.getIsActive()) {
              otherGate.deactivate();
            }
          }
          this.events.emit('gateActivated', gate, resolvedOperation, scoreFactor, soldiersDelta, rowIndex);
        }
      );

      rowGates.push(gate);
      this.activeGates.push(gate);
    }

    this.gateRows.set(rowIndex, rowGates);
    this.events.emit('gateRowSpawned', rowGates, rowIndex);
  }

  private checkWeaponBoxSpawns(): void {
    const events = this.generatedLevel.weaponBoxEvents;
    while (this.weaponBoxSpawnIndex < events.length) {
      const event = events[this.weaponBoxSpawnIndex];
      if (this.progressDistance < this.getAdjustedTriggerDistance(event)) break;
      this.maybeSpawnWeaponBox(event, this.weaponBoxSpawnIndex);
      this.weaponBoxSpawnIndex += 1;
    }
  }

  private maybeSpawnWeaponBox(event: CompiledWeaponBoxEvent, boxIndex: number): void {
    const chance = event.chance;
    const roll = getDeterministicRollForEvent(this.seed, event.id);
    if (roll > chance) return;

    const nextWeaponIndex = getNextWeaponIndexForLevel(this.currentWeaponIndex, this.level);
    if (nextWeaponIndex === null || nextWeaponIndex >= WEAPON_SEQUENCE.length) return;

    const x = getEnemyLane4X(event.lane, this.trackWidth, this.trackPadding);
    const y = -WeaponBox.SIZE;
    const scrollSpeed = this.getScrollSpeed();
    const weaponBox = new WeaponBox(this.scene, {
      x,
      y,
      hp: event.baseHp,
      nextWeapon: WEAPON_SEQUENCE[nextWeaponIndex],
    });
    weaponBox.setScrollSpeed(scrollSpeed);
    weaponBox.on('destroyed', (weapon: WeaponType) => {
      // Multiple boxes can be alive at once. Recording the actual offered tier
      // prevents two duplicate rifle boxes from falsely advancing this state
      // to machine gun or rocket.
      this.currentWeaponIndex = Math.max(this.currentWeaponIndex, nextWeaponIndex);
      this.events.emit('weaponBoxDestroyed', weaponBox, weapon, boxIndex);
    });
    this.activeWeaponBoxes.push(weaponBox);
    this.events.emit('weaponBoxSpawned', weaponBox, boxIndex);
  }

  private updateActiveEnemies(delta: number): void {
    updateAndCompactEntities(
      this.activeEnemies,
      delta,
      (enemy) => enemy.getIsAlive() && enemy.active
    );
  }

  private updateActiveGates(delta: number): void {
    updateAndCompactEntities(this.activeGates, delta, (gate) => gate.getIsActive());
  }

  private updateActiveWeaponBoxes(delta: number): void {
    updateAndCompactEntities(this.activeWeaponBoxes, delta, (box) => box.getIsActive());
  }

  private getFromPool(x: number, y: number): Enemy {
    const pooledEnemy = this.enemyPool.find((enemy) => !enemy.active);
    if (pooledEnemy) {
      pooledEnemy.resetForReuse(x, y);
      return pooledEnemy;
    }
    return new Enemy(this.scene, { x, y });
  }

  private returnToPool(enemy: Enemy): void {
    if (enemy instanceof EliteEnemy) return;
    if (!this.enemyPool.includes(enemy)) return;
    enemy.setActive(false);
    enemy.setVisible(false);
    enemy.setPosition(-100, -100);
  }

  private checkLevelComplete(): void {
    if (this.isLevelComplete) return;
    const allEnemiesSpawned = this.enemySpawnIndex >= this.generatedLevel.enemyEvents.length;
    const allGatesSpawned = this.gateSpawnIndex >= this.generatedLevel.gateEvents.length;
    const allBoxesSpawned = this.weaponBoxSpawnIndex >= this.generatedLevel.weaponBoxEvents.length;
    const pastTrackEnd = this.progressDistance >= this.generatedLevel.trackLength;

    if (canResolveLevel({
      pastTrackEnd,
      allEnemiesSpawned,
      allGatesSpawned,
      allWeaponBoxesSpawned: allBoxesSpawned,
      activeEnemyCount: this.activeEnemies.length,
      activeGateCount: this.activeGates.length,
      activeWeaponBoxCount: this.activeWeaponBoxes.length,
      finaleDefeated: this.finaleDefeated,
    })) {
      this.isLevelComplete = true;
      this.events.emit('levelComplete', this.level, this.tier);
    }
  }

  private getAdjustedTriggerDistance(
    event: CompiledEnemyEvent | CompiledGateEvent | CompiledWeaponBoxEvent
  ): number {
    return event.triggerDistance;
  }

  getActiveEnemies(): Enemy[] {
    return this.activeEnemies;
  }

  getActiveGates(): Gate[] {
    return this.activeGates;
  }

  getActiveWeaponBoxes(): WeaponBox[] {
    return this.activeWeaponBoxes;
  }

  getProgressDistance(): number {
    return this.progressDistance;
  }

  getTrackLength(): number {
    return this.generatedLevel.trackLength;
  }

  getProgressPercent(): number {
    if (this.generatedLevel.trackLength <= 0) return 0;
    return Math.min(1, this.progressDistance / this.generatedLevel.trackLength);
  }

  getIsLevelComplete(): boolean {
    return this.isLevelComplete;
  }

  getCurrentWeaponIndex(): number {
    return this.currentWeaponIndex;
  }

  getIsFinaleDefeated(): boolean {
    return this.finaleDefeated;
  }

  getTargetEnemyCount(): number {
    return this.generatedLevel.targetEnemyCount;
  }

  getEvents(): Phaser.Events.EventEmitter {
    return this.events;
  }

  getCurrentSegmentId(): string {
    return this.currentSegmentId;
  }

  getLastArmyRatio(): number {
    return this.lastArmyRatio;
  }

  getLastDdaDelta(): number {
    return this.lastDdaDelta;
  }

  getSeed(): number {
    return this.seed;
  }

  reset(level: number, tier: number = 1): void {
    this.level = level;
    this.tier = tier;
    this.seed = createDeterministicLevelSeed(level, tier);
    this.generatedLevel = generateLevelConfig(level, tier, this.seed);
    this.progressDistance = 0;
    this.isLevelComplete = false;
    this.finaleDefeated = false;
    this.currentWeaponIndex = 0;
    this.enemySpawnIndex = 0;
    this.gateSpawnIndex = 0;
    this.weaponBoxSpawnIndex = 0;
    this.currentSegmentId = 'onboard';
    this.lastArmyRatio = 1;
    this.lastDdaDelta = 0;
    this.nextSegmentAdjustmentIndex = 0;
    this.activeEnemies = [];
    this.activeGates = [];
    this.activeWeaponBoxes = [];
    this.gateRows.clear();
  }

  destroy(): void {
    for (const enemy of this.enemyPool) {
      enemy.destroy();
    }
    this.enemyPool = [];
    this.activeEnemies = [];
    this.activeGates = [];
    this.activeWeaponBoxes = [];
    this.gateRows.clear();
    this.events.removeAllListeners();
  }
}
